import { ISelectMultiOption, SelectMulti } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { TestingFilter } from './TestingFilter';
import { ISegmentFilter } from 'sections/offsite-campaigns/entities/offsiteCampaign';
import { useRemoveSegmentFilter } from './hooks/useRemoveSegmentFilter';
import { useUpdateFilterList } from './hooks/useUpdateFilterList';
import { useBlocklistAllowlist } from 'sections/client-management/ui/pages/client-settings/blocklist-allowlist/components/hooks/useBlocklistAllowlist';
import { useOffsiteCampaignEditorState } from 'sections/offsite-campaigns/offsite-campaign-editor/context/UseOffsiteCampaignEditorContext';
import { useCallback, useEffect, useState } from 'react';
import { IBlocklistAllowlistItem } from 'sections/client-management/ui/pages/client-settings/blocklist-allowlist/entities/blocklist-allowlist-constants';
import { listOfStringsToMultiSelectOptions } from 'components/utils/getSelectOptions';
import {
  channelAll,
  listTypeAllowlist,
} from 'sections/client-management/ui/pages/client-settings/blocklist-allowlist/entities/blocklist-allowlist-constants';

export interface SMSTestingFilterProps {
  segmentId: string;
  segmentFilter: ISegmentFilter;
}

const mapDataToSelectMultiOptions = (e: IBlocklistAllowlistItem): ISelectMultiOption<string> => {
  let label = `${e.name} <${e.value}>`;
  if (e.name === undefined) {
    label = e.value;
  }
  return {
    label,
    value: e.value,
  };
};

export const SMSTestingFilter = ({ segmentId, segmentFilter }: SMSTestingFilterProps) => {
  const { t } = useTranslation();
  const { site } = useOffsiteCampaignEditorState();
  const queryString = `productType=${channelAll}&listType=${listTypeAllowlist}`;
  const { data, isSuccess } = useBlocklistAllowlist(site.id, queryString);

  const [allowListOptions, setAllowListOptions] = useState<ISelectMultiOption<string>[]>([]);
  const [hasFocused, setHasFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const multiSelectOptions = listOfStringsToMultiSelectOptions(segmentFilter.options);

  useEffect(() => {
    if (isSuccess && data.length > 0) {
      const mobileData = data.filter((item: IBlocklistAllowlistItem) => item.type === 'mobile');
      setAllowListOptions(mobileData.map(mapDataToSelectMultiOptions));
    }
  }, [isSuccess, data]);

  const deleteFilter = useRemoveSegmentFilter(segmentId, segmentFilter);
  const updateFilterList = useUpdateFilterList(segmentId, segmentFilter);

  const onElementFocused = useCallback(() => {
    setHasFocused(true);
    setIsDropdownOpen(true);
  }, []);

  const onElementBlur = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  return (
    <TestingFilter
      icon="mobile"
      title={t('campaigns:offsiteEditor.segments.filters.testing.smsName')}
      detail={t('campaigns:offsiteEditor.segments.filters.testing.detail')}
      tooltip={t('campaigns:offsiteEditor.segments.filters.delete.iconAriaLabel')}
      deleteButtonAriaLabel={t('campaigns:offsiteEditor.segments.filters.delete.iconAriaLabel')}
      deleteButtonTestId="delete-segment-filter-button"
      testId="accepted-urls"
      onRemove={deleteFilter}
    >
      <SelectMulti
        key="AcceptedURLs={true}"
        label={t('campaigns:offsiteEditor.segments.filters.testing.smsList.label')}
        ariaLabel={t('campaigns:offsiteEditor.segments.filters.testing.smsList.label')}
        placeholder={t('campaigns:offsiteEditor.segments.filters.testing.smsList.placeholder')}
        creatable={false}
        options={allowListOptions}
        value={multiSelectOptions}
        onChange={updateFilterList}
        onFocus={onElementFocused}
        onBlur={onElementBlur}
        validationState={!segmentFilter.options.length && hasFocused && !isDropdownOpen ? 'warning' : null}
        validationMessage={
          !segmentFilter.options.length && hasFocused && !isDropdownOpen
            ? t('campaigns:offsiteEditor.segments.filters.testing.smsValidationMessage')
            : null
        }
        tooltipHeading={t('campaigns:offsiteEditor.segments.filters.testing.smsList.tooltipHeading')}
        tooltipDescription={t('campaigns:offsiteEditor.segments.filters.testing.smsList.tooltipDescription')}
      />
    </TestingFilter>
  );
};
